import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { Footer } from '@/components/organisms/Footer';
import { apiService } from '@/services/api';
import type { MarketCategory, MarketListingSummary } from '@/types/market';
import { ListingCard, useSavedIds } from './MarketShared';
import styles from './Market.module.scss';

export default function MarketHome() {
  const navigate = useNavigate();
  const { savedIds, toggleSave } = useSavedIds();
  const [q, setQ] = useState('');
  const [categories, setCategories] = useState<MarketCategory[]>([]);
  const [recent, setRecent] = useState<MarketListingSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    (async () => {
      setLoading(true);
      try {
        const [forSale, services, listingsRes] = await Promise.all([
          apiService.mktGetCategories('for_sale'),
          apiService.mktGetCategories('service'),
          apiService.mktGetListings({}),
        ]);
        if (!mounted.current) return;
        setCategories([...forSale, ...services]);
        setRecent(listingsRes.listings);
        setError(null);
      } catch (e: any) {
        if (mounted.current) setError(e?.message || 'Failed to load the marketplace.');
      } finally {
        if (mounted.current) setLoading(false);
      }
    })();
    return () => { mounted.current = false; };
  }, []);

  const forSale = categories.filter((c) => c.kind === 'for_sale');
  const services = categories.filter((c) => c.kind === 'service');

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const query = q.trim();
    navigate(`/customer/market/browse${query ? `?q=${encodeURIComponent(query)}` : ''}`);
  };

  const openCategory = (c: MarketCategory) => {
    navigate(`/customer/market/browse?kind=${encodeURIComponent(c.kind)}&category=${encodeURIComponent(c.code)}`);
  };

  return (
    <>
      <DashboardHeader />
      <div className={styles.container}>
        <div className={styles.topBar}>
          <h1 className={styles.title}>Marketplace</h1>
          <span className={styles.spacer} />
          <button type="button" className={styles.secondaryBtn} onClick={() => navigate('/customer/market/saved')}>
            ♥ Saved
          </button>
          <button type="button" className={styles.primaryBtn} onClick={() => navigate('/customer/market/post')}>
            + Post an ad
          </button>
        </div>
        <p className={styles.subtitle}>Buy &amp; sell, or find a service provider near you.</p>

        <form className={styles.searchRow} onSubmit={submitSearch}>
          <input
            className={styles.searchInput}
            placeholder="Search listings…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button type="submit" className={styles.secondaryBtn}>Search</button>
        </form>

        <div className={styles.filters} style={{ marginTop: 8 }}>
          <button type="button" className={styles.secondaryBtn} onClick={() => navigate('/customer/market/browse')}>Browse all</button>
          <button type="button" className={styles.secondaryBtn} onClick={() => navigate('/customer/market/providers')}>Service directory</button>
          <button type="button" className={styles.secondaryBtn} onClick={() => navigate('/customer/market/my')}>My listings</button>
          <button type="button" className={styles.secondaryBtn} onClick={() => navigate('/customer/market/messages')}>Messages</button>
        </div>

        {error && <p className={styles.error}>{error}</p>}
        {loading && <p className={styles.muted}><span className={styles.spinner} /> Loading…</p>}

        {!loading && (
          <>
            {forSale.length > 0 && (
              <>
                <h2 className={styles.sectionTitle}>🛍️ For sale</h2>
                <div className={styles.tileGrid}>
                  {forSale.map((c) => (
                    <div key={c.code} className={styles.tile} onClick={() => openCategory(c)} role="button" tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') openCategory(c); }}>
                      <div className={styles.tileIcon}>{c.icon || '📦'}</div>
                      <div className={styles.tileName}>{c.name}</div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {services.length > 0 && (
              <>
                <h2 className={styles.sectionTitle}>🛠️ Services</h2>
                <div className={styles.tileGrid}>
                  {services.map((c) => (
                    <div key={c.code} className={styles.tile} onClick={() => openCategory(c)} role="button" tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') openCategory(c); }}>
                      <div className={styles.tileIcon}>{c.icon || '🛠️'}</div>
                      <div className={styles.tileName}>{c.name}</div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <h2 className={styles.sectionTitle}>🆕 Recent listings</h2>
            {recent.length === 0 ? (
              <div className={styles.empty}>
                <div className={styles.emptyIcon}>🏷️</div>
                Nothing listed yet. Be the first to post an ad.
              </div>
            ) : (
              <div className={styles.grid}>
                {recent.map((l) => (
                  <ListingCard key={l.id} listing={l} onClick={() => navigate(`/customer/market/listing/${l.id}`)}
                    saved={savedIds.has(l.id)} onToggleSave={() => toggleSave(l.id)} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </>
  );
}
